import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Package, Plus, Trash2, Send, Building2, Warehouse, TrendingDown, AlertTriangle,
  RefreshCw, MapPin, Undo2, ArrowRightLeft, Download, FileSpreadsheet,
  PlusCircle, MinusCircle, Ban, CheckCircle2, History, Edit2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InventoryPage = () => {
  const { t, isRTL } = useLanguage();
  const { isAdmin, isAdminOrInventory, isSuperAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  // Dialogs
  const [createWhDialog, setCreateWhDialog] = useState(false);
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [distributeDialog, setDistributeDialog] = useState(false);
  const [returnDialog, setReturnDialog] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  const [createLocDialog, setCreateLocDialog] = useState(false);
  const [editLocDialog, setEditLocDialog] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  
  // Forms
  const [newWh, setNewWh] = useState({ name: '', type: 'REGIONAL' });
  const [newLoc, setNewLoc] = useState({ name: '', type: 'hospital', address: '' });
  const [adjustForm, setAdjustForm] = useState({ warehouse_id: '', quantity: '', operation: 'add', notes: '' });
  const [transferForm, setTransferForm] = useState({ from_warehouse_id: '', to_warehouse_id: '', quantity: '', notes: '' });
  const [distributeForm, setDistributeForm] = useState({ from_warehouse_id: '', to_hospital_id: '', quantity: '', notes: '' });
  const [returnForm, setReturnForm] = useState({ return_type: 'hospital_to_warehouse', from_hospital_id: '', from_warehouse_id: '', to_warehouse_id: '', quantity: '', notes: '' });
  const [exportFilters, setExportFilters] = useState({ start_date: '', end_date: '', txn_type: '', from_warehouse_id: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ovRes, whRes, locRes, txnRes] = await Promise.all([
        axios.get(`${API}/inventory/overview`),
        axios.get(`${API}/warehouses?include_disabled=true`),
        axios.get(`${API}/locations?include_disabled=true`),
        axios.get(`${API}/inventory/transactions?limit=50`)
      ]);
      setOverview(ovRes.data);
      setWarehouses(whRes.data);
      setHospitals(locRes.data);
      setTransactions(txnRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error(isRTL ? 'خطأ في جلب البيانات' : 'Error fetching data');
    } finally { setLoading(false); }
  };

  const handleCreateWarehouse = async () => {
    if (!newWh.name) { toast.error(isRTL ? 'أدخل اسم المستودع' : 'Enter warehouse name'); return; }
    try {
      await axios.post(`${API}/warehouses`, newWh);
      toast.success(isRTL ? 'تم إنشاء المستودع' : 'Warehouse created');
      setCreateWhDialog(false); setNewWh({ name: '', type: 'REGIONAL' }); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleToggleWarehouse = async (id, action) => {
    try {
      await axios.put(`${API}/warehouses/${id}/${action}`);
      toast.success(action === 'disable' ? (isRTL ? 'تم تعطيل المستودع' : 'Warehouse disabled') : (isRTL ? 'تم تفعيل المستودع' : 'Warehouse enabled'));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDeleteWarehouse = async (id) => {
    if (!window.confirm(isRTL ? 'هل أنت متأكد من حذف هذا المستودع؟' : 'Are you sure you want to delete this warehouse?')) return;
    try {
      await axios.delete(`${API}/warehouses/${id}`);
      toast.success(isRTL ? 'تم حذف المستودع' : 'Warehouse deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm(isRTL ? 'هل أنت متأكد من حذف موقع التوزيع هذا؟' : 'Are you sure you want to delete this distribution location?')) return;
    try {
      await axios.delete(`${API}/locations/${id}`);
      toast.success(isRTL ? 'تم حذف الموقع' : 'Location deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleAdjust = async () => {
    if (!adjustForm.warehouse_id || !adjustForm.quantity || parseFloat(adjustForm.quantity) <= 0) {
      toast.error(isRTL ? 'يرجى ملء جميع الحقول' : 'Fill all fields'); return;
    }
    try {
      await axios.post(`${API}/inventory/adjust`, { ...adjustForm, quantity: parseFloat(adjustForm.quantity) });
      toast.success(isRTL ? 'تم تعديل المخزون' : 'Stock adjusted');
      setAdjustDialog(false); setAdjustForm({ warehouse_id: '', quantity: '', operation: 'add', notes: '' }); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleTransfer = async () => {
    if (!transferForm.from_warehouse_id || !transferForm.to_warehouse_id || !transferForm.quantity) {
      toast.error(isRTL ? 'يرجى ملء جميع الحقول' : 'Fill all fields'); return;
    }
    try {
      await axios.post(`${API}/inventory/transfer`, { ...transferForm, quantity: parseFloat(transferForm.quantity) });
      toast.success(isRTL ? 'تم النقل بنجاح' : 'Transfer successful');
      setTransferDialog(false); setTransferForm({ from_warehouse_id: '', to_warehouse_id: '', quantity: '', notes: '' }); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDistribute = async () => {
    if (!distributeForm.from_warehouse_id || !distributeForm.to_hospital_id || !distributeForm.quantity) {
      toast.error(isRTL ? 'يرجى ملء جميع الحقول' : 'Fill all fields'); return;
    }
    try {
      await axios.post(`${API}/inventory/distribute-to-hospital`, { ...distributeForm, quantity: parseFloat(distributeForm.quantity) });
      toast.success(isRTL ? 'تم التوزيع بنجاح' : 'Distribution successful');
      setDistributeDialog(false); setDistributeForm({ from_warehouse_id: '', to_hospital_id: '', quantity: '', notes: '' }); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleReturn = async () => {
    if (!returnForm.to_warehouse_id || !returnForm.quantity) {
      toast.error(isRTL ? 'يرجى ملء جميع الحقول' : 'Fill all fields'); return;
    }
    try {
      const payload = {
        to_warehouse_id: returnForm.to_warehouse_id,
        quantity: parseFloat(returnForm.quantity),
        notes: returnForm.notes
      };
      if (returnForm.return_type === 'hospital_to_warehouse') {
        payload.from_hospital_id = returnForm.from_hospital_id;
      } else {
        payload.from_warehouse_id = returnForm.from_warehouse_id;
      }
      await axios.post(`${API}/inventory/return-stock`, payload);
      toast.success(isRTL ? 'تم الإرجاع بنجاح' : 'Return successful');
      setReturnDialog(false); setReturnForm({ return_type: 'hospital_to_warehouse', from_hospital_id: '', from_warehouse_id: '', to_warehouse_id: '', quantity: '', notes: '' }); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (exportFilters.start_date) params.append('start_date', exportFilters.start_date);
      if (exportFilters.end_date) params.append('end_date', exportFilters.end_date);
      if (exportFilters.txn_type && exportFilters.txn_type !== 'all') params.append('txn_type', exportFilters.txn_type);
      if (exportFilters.from_warehouse_id && exportFilters.from_warehouse_id !== 'all') params.append('from_warehouse_id', exportFilters.from_warehouse_id);
      const response = await axios.get(`${API}/export/inventory-report?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory_report_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(isRTL ? 'تم التحميل' : 'Downloaded');
      setExportDialog(false);
    } catch (e) { toast.error(isRTL ? 'خطأ في التحميل' : 'Download error'); }
  };

  const handleCreateLocation = async () => {
    if (!newLoc.name) { toast.error(isRTL ? 'أدخل اسم الموقع' : 'Enter location name'); return; }
    try {
      await axios.post(`${API}/locations`, newLoc);
      toast.success(isRTL ? 'تم إنشاء موقع التوزيع' : 'Distribution location created');
      setCreateLocDialog(false); setNewLoc({ name: '', type: 'hospital', address: '' }); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleEditLocation = async () => {
    if (!editingLoc?.name) { toast.error(isRTL ? 'أدخل الاسم' : 'Enter name'); return; }
    try {
      await axios.put(`${API}/locations/${editingLoc.id}`, { name: editingLoc.name, type: editingLoc.type, address: editingLoc.address });
      toast.success(isRTL ? 'تم تحديث الموقع' : 'Location updated');
      setEditLocDialog(false); setEditingLoc(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleToggleLocation = async (id, action) => {
    try {
      await axios.put(`${API}/locations/${id}/${action}`);
      toast.success(action === 'disable' ? (isRTL ? 'تم تعطيل الموقع' : 'Location disabled') : (isRTL ? 'تم تفعيل الموقع' : 'Location enabled'));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleClearHistory = async () => {
    if (!window.confirm(isRTL ? t('clear_history_confirm') : t('clear_history_confirm'))) return;
    try {
      const r = await axios.delete(`${API}/inventory/transactions/clear`);
      toast.success(isRTL ? `تم مسح ${r.data.deleted_count} سجل` : `Cleared ${r.data.deleted_count} records`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const activeWarehouses = warehouses.filter(w => w.status === 'active');
  const activeLocations = hospitals.filter(h => h.status !== 'disabled');
  const mainWh = warehouses.find(w => w.type === 'MAIN');

  const txnTypeLabel = (type) => {
    const map = { TRANSFER: isRTL ? 'نقل' : 'Transfer', DISTRIBUTION: isRTL ? 'توزيع' : 'Distribution', RETURN: isRTL ? 'إرجاع' : 'Return', ADJUSTMENT: isRTL ? 'تعديل' : 'Adjustment' };
    return map[type] || type;
  };
  const txnTypeColor = (type) => {
    const map = { TRANSFER: 'bg-blue-100 text-blue-700', DISTRIBUTION: 'bg-emerald-100 text-emerald-700', RETURN: 'bg-amber-100 text-amber-700', ADJUSTMENT: 'bg-purple-100 text-purple-700' };
    return map[type] || 'bg-slate-100 text-slate-700';
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="h-8 w-8 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1">{isRTL ? 'إدارة المخزون' : 'Inventory Management'}</h1>
          <p className="text-slate-500">{isRTL ? 'نظام المستودعات المتعدد' : 'Multi-Warehouse System'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setExportDialog(true)} variant="outline" className="gap-2">
            <FileSpreadsheet className="h-4 w-4" />{isRTL ? 'تقرير Excel' : 'Excel Report'}
          </Button>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />{isRTL ? 'تحديث' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-600">{isRTL ? 'إجمالي المستودعات' : 'Total Warehouse Stock'}</p>
                <p className="text-3xl font-bold text-indigo-700">{overview?.summary?.total_warehouse_stock || 0}</p>
              </div>
              <Warehouse className="h-9 w-9 text-indigo-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-sky-50 to-white border-sky-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-sky-600">{isRTL ? 'إجمالي مواقع التوزيع' : 'Total Distribution Stock'}</p>
                <p className="text-3xl font-bold text-sky-700">{overview?.summary?.total_hospital_stock || 0}</p>
              </div>
              <Building2 className="h-9 w-9 text-sky-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">{isRTL ? 'إجمالي المصروف' : 'Total Dispensed'}</p>
                <p className="text-3xl font-bold text-red-700">{overview?.summary?.total_dispensed || 0}</p>
              </div>
              <TrendingDown className="h-9 w-9 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setAdjustDialog(true)} className="gap-2 bg-purple-500 hover:bg-purple-600"><PlusCircle className="h-4 w-4" />{isRTL ? 'تعديل مخزون' : 'Adjust Stock'}</Button>
        <Button onClick={() => setTransferDialog(true)} className="gap-2 bg-blue-500 hover:bg-blue-600"><ArrowRightLeft className="h-4 w-4" />{isRTL ? 'نقل بين المستودعات' : 'Transfer'}</Button>
        <Button onClick={() => setDistributeDialog(true)} className="gap-2 bg-emerald-500 hover:bg-emerald-600"><Send className="h-4 w-4" />{isRTL ? 'توزيع على موقع' : 'Distribute'}</Button>
        <Button onClick={() => setReturnDialog(true)} className="gap-2 bg-amber-500 hover:bg-amber-600"><Undo2 className="h-4 w-4" />{isRTL ? 'إرجاع' : 'Return'}</Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="warehouses" className="space-y-4">
        <TabsList>
          <TabsTrigger value="warehouses" className="gap-2"><Warehouse className="h-4 w-4" />{isRTL ? 'المستودعات' : 'Warehouses'}</TabsTrigger>
          <TabsTrigger value="hospitals" className="gap-2"><Building2 className="h-4 w-4" />{isRTL ? 'مواقع التوزيع' : 'Distribution Locations'}</TabsTrigger>
          <TabsTrigger value="transactions" className="gap-2"><History className="h-4 w-4" />{isRTL ? 'السجل' : 'Transactions'}</TabsTrigger>
        </TabsList>

        {/* Warehouses Tab */}
        <TabsContent value="warehouses">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><Warehouse className="h-5 w-5 text-indigo-500" />{isRTL ? 'المستودعات' : 'Warehouses'}</CardTitle>
              <Button onClick={() => setCreateWhDialog(true)} className="gap-2 bg-indigo-500 hover:bg-indigo-600"><Plus className="h-4 w-4" />{isRTL ? 'إضافة مستودع' : 'Add Warehouse'}</Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isRTL ? 'الاسم' : 'Name'}</TableHead>
                    <TableHead>{isRTL ? 'النوع' : 'Type'}</TableHead>
                    <TableHead>{isRTL ? 'المخزون الحالي' : 'Current Stock'}</TableHead>
                    <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead className="text-center">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {warehouses.map(wh => (
                    <TableRow key={wh.id} className={wh.status === 'disabled' ? 'opacity-50' : ''}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Warehouse className="h-4 w-4 text-indigo-400" />
                          {wh.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={wh.type === 'MAIN' ? 'bg-indigo-500' : wh.type === 'REGIONAL' ? 'bg-sky-500' : 'bg-slate-500'}>
                          {wh.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono font-medium">{wh.current_stock || 0} {isRTL ? 'حبة' : 'Tablet'}</TableCell>
                      <TableCell>
                        <Badge className={wh.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {wh.status === 'active' ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'معطل' : 'Disabled')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {wh.type !== 'MAIN' && (
                          <div className="flex items-center justify-center gap-1">
                            {wh.status === 'active' ? (
                              <Button variant="ghost" size="sm" onClick={() => handleToggleWarehouse(wh.id, 'disable')} className="h-8 w-8 p-0 hover:text-red-600" title={isRTL ? 'تعطيل' : 'Disable'}>
                                <Ban className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => handleToggleWarehouse(wh.id, 'enable')} className="h-8 w-8 p-0 hover:text-emerald-600" title={isRTL ? 'تفعيل' : 'Enable'}>
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteWarehouse(wh.id)} className="h-8 w-8 p-0 hover:text-red-600" title={isRTL ? 'حذف' : 'Delete'}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distribution Locations Tab */}
        <TabsContent value="hospitals">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><Building2 className="h-5 w-5 text-sky-500" />{isRTL ? 'مواقع التوزيع' : 'Distribution Locations'}</CardTitle>
              <Button onClick={() => setCreateLocDialog(true)} className="gap-2 bg-sky-500 hover:bg-sky-600"><Plus className="h-4 w-4" />{isRTL ? 'إضافة موقع توزيع' : 'Add Distribution Location'}</Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isRTL ? 'الاسم' : 'Name'}</TableHead>
                    <TableHead>{isRTL ? 'النوع' : 'Type'}</TableHead>
                    <TableHead>{isRTL ? 'المخزون' : 'Stock'}</TableHead>
                    <TableHead>{isRTL ? 'المصروف' : 'Dispensed'}</TableHead>
                    <TableHead>{isRTL ? 'المتبقي' : 'Remaining'}</TableHead>
                    <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead className="text-center">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hospitals.map(h => {
                    const ovData = (overview?.hospitals || []).find(oh => oh.id === h.id) || h;
                    const locStatus = h.status || 'active';
                    return (
                    <TableRow key={h.id} className={locStatus === 'disabled' ? 'opacity-50' : ''}>
                      <TableCell className="font-medium"><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-sky-400" />{h.name}</div></TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {h.type === 'hospital' ? (isRTL ? 'مستشفى' : 'Hospital') : h.type === 'health_center' ? (isRTL ? 'مركز صحي' : 'Health Center') : h.type === 'temporary_site' ? (isRTL ? 'موقع مؤقت' : 'Temporary Site') : h.type === 'committee' ? (isRTL ? 'لجنة' : 'Committee') : h.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono">{ovData.stock_quantity || 0}</TableCell>
                      <TableCell className="font-mono text-amber-600">{ovData.dispensed || 0}</TableCell>
                      <TableCell><span className={`font-mono ${(ovData.remaining || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{ovData.remaining || 0}</span></TableCell>
                      <TableCell>
                        <Badge className={locStatus === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {locStatus === 'active' ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'معطل' : 'Disabled')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setEditingLoc({id: h.id, name: h.name, type: h.type, address: h.address || ''}); setEditLocDialog(true); }} className="h-7 w-7 p-0 hover:text-sky-600" title={isRTL ? 'تعديل' : 'Edit'}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          {locStatus === 'active' ? (
                            <Button variant="ghost" size="sm" onClick={() => handleToggleLocation(h.id, 'disable')} className="h-7 w-7 p-0 hover:text-red-600" title={isRTL ? 'تعطيل' : 'Disable'}>
                              <Ban className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => handleToggleLocation(h.id, 'enable')} className="h-7 w-7 p-0 hover:text-emerald-600" title={isRTL ? 'تفعيل' : 'Enable'}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteLocation(h.id)} className="h-7 w-7 p-0 hover:text-red-600" title={isRTL ? 'حذف' : 'Delete'}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {hospitals.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">{t('no_data')}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><History className="h-5 w-5 text-slate-500" />{isRTL ? 'سجل المعاملات' : 'Transaction History'}</CardTitle>
              {isSuperAdmin() && transactions.length > 0 && (
                <Button onClick={handleClearHistory} variant="outline" size="sm" className="gap-2 text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                  {t('clear_history')}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{isRTL ? 'النوع' : 'Type'}</TableHead>
                      <TableHead>{isRTL ? 'من' : 'From'}</TableHead>
                      <TableHead>{isRTL ? 'إلى' : 'To'}</TableHead>
                      <TableHead>{isRTL ? 'الكمية' : 'Qty'}</TableHead>
                      <TableHead>{isRTL ? 'بواسطة' : 'By'}</TableHead>
                      <TableHead>{isRTL ? 'التاريخ' : 'Date'}</TableHead>
                      <TableHead>{isRTL ? 'ملاحظات' : 'Notes'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map(txn => (
                      <TableRow key={txn.id}>
                        <TableCell><Badge className={txnTypeColor(txn.type)}>{txnTypeLabel(txn.type)}</Badge></TableCell>
                        <TableCell className="text-sm">{txn.from_name || '-'}</TableCell>
                        <TableCell className="text-sm">{txn.to_name || '-'}</TableCell>
                        <TableCell className="font-mono font-medium">{txn.quantity}</TableCell>
                        <TableCell className="text-sm">{txn.performed_by}</TableCell>
                        <TableCell className="text-xs text-slate-500">{txn.created_at?.slice(0, 16).replace('T', ' ')}</TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-[150px] truncate">{txn.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {transactions.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">{t('no_data')}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* === DIALOGS === */}

      {/* Create Warehouse */}
      <Dialog open={createWhDialog} onOpenChange={setCreateWhDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRTL ? 'إنشاء مستودع جديد' : 'Create New Warehouse'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'اسم المستودع' : 'Warehouse Name'}</Label>
              <Input value={newWh.name} onChange={(e) => setNewWh({...newWh, name: e.target.value})} placeholder={isRTL ? 'مثال: مستودع الدوحة' : 'e.g. Doha Warehouse'} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'النوع' : 'Type'}</Label>
              <Select value={newWh.type} onValueChange={(v) => setNewWh({...newWh, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="REGIONAL">{isRTL ? 'إقليمي' : 'Regional'}</SelectItem>
                  <SelectItem value="TEMP">{isRTL ? 'مؤقت' : 'Temporary'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateWhDialog(false)}>{t('cancel')}</Button>
            <Button onClick={handleCreateWarehouse} className="bg-indigo-500 hover:bg-indigo-600">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment */}
      <Dialog open={adjustDialog} onOpenChange={setAdjustDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRTL ? 'تعديل المخزون' : 'Adjust Stock'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'المستودع' : 'Warehouse'}</Label>
              <Select value={adjustForm.warehouse_id} onValueChange={(v) => setAdjustForm({...adjustForm, warehouse_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر' : 'Select'} /></SelectTrigger>
                <SelectContent>{activeWarehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.current_stock})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'العملية' : 'Operation'}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setAdjustForm({...adjustForm, operation: 'add'})} className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 ${adjustForm.operation === 'add' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200'}`}>
                  <PlusCircle className="h-4 w-4" />{isRTL ? 'إضافة' : 'Add'}
                </button>
                <button type="button" onClick={() => setAdjustForm({...adjustForm, operation: 'reduce'})} className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 ${adjustForm.operation === 'reduce' ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200'}`}>
                  <MinusCircle className="h-4 w-4" />{isRTL ? 'تقليل' : 'Reduce'}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الكمية' : 'Quantity'}</Label>
              <Input type="number" min="1" value={adjustForm.quantity} onChange={(e) => setAdjustForm({...adjustForm, quantity: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Input value={adjustForm.notes} onChange={(e) => setAdjustForm({...adjustForm, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialog(false)}>{t('cancel')}</Button>
            <Button onClick={handleAdjust} className={adjustForm.operation === 'add' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}>{adjustForm.operation === 'add' ? (isRTL ? 'إضافة' : 'Add') : (isRTL ? 'تقليل' : 'Reduce')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRTL ? 'نقل بين المستودعات' : 'Warehouse Transfer'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'من مستودع' : 'From Warehouse'}</Label>
              <Select value={transferForm.from_warehouse_id} onValueChange={(v) => setTransferForm({...transferForm, from_warehouse_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر' : 'Select'} /></SelectTrigger>
                <SelectContent>{activeWarehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.current_stock})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'إلى مستودع' : 'To Warehouse'}</Label>
              <Select value={transferForm.to_warehouse_id} onValueChange={(v) => setTransferForm({...transferForm, to_warehouse_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر' : 'Select'} /></SelectTrigger>
                <SelectContent>{activeWarehouses.filter(w => w.id !== transferForm.from_warehouse_id).map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.current_stock})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الكمية' : 'Quantity'}</Label>
              <Input type="number" min="1" value={transferForm.quantity} onChange={(e) => setTransferForm({...transferForm, quantity: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Input value={transferForm.notes} onChange={(e) => setTransferForm({...transferForm, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)}>{t('cancel')}</Button>
            <Button onClick={handleTransfer} className="bg-blue-500 hover:bg-blue-600 gap-2"><ArrowRightLeft className="h-4 w-4" />{isRTL ? 'نقل' : 'Transfer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Distribute */}
      <Dialog open={distributeDialog} onOpenChange={setDistributeDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRTL ? 'توزيع على موقع' : 'Distribute to Location'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'من مستودع' : 'From Warehouse'}</Label>
              <Select value={distributeForm.from_warehouse_id} onValueChange={(v) => setDistributeForm({...distributeForm, from_warehouse_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر' : 'Select'} /></SelectTrigger>
                <SelectContent>{activeWarehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.current_stock})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'إلى موقع توزيع' : 'To Distribution Location'}</Label>
              <Select value={distributeForm.to_hospital_id} onValueChange={(v) => setDistributeForm({...distributeForm, to_hospital_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر' : 'Select'} /></SelectTrigger>
                <SelectContent>{activeLocations.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الكمية' : 'Quantity'}</Label>
              <Input type="number" min="1" value={distributeForm.quantity} onChange={(e) => setDistributeForm({...distributeForm, quantity: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Input value={distributeForm.notes} onChange={(e) => setDistributeForm({...distributeForm, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDistributeDialog(false)}>{t('cancel')}</Button>
            <Button onClick={handleDistribute} className="bg-emerald-500 hover:bg-emerald-600 gap-2"><Send className="h-4 w-4" />{isRTL ? 'توزيع' : 'Distribute'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return */}
      <Dialog open={returnDialog} onOpenChange={setReturnDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRTL ? 'إرجاع مخزون' : 'Return Stock'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'نوع الإرجاع' : 'Return Type'}</Label>
              <Select value={returnForm.return_type} onValueChange={(v) => setReturnForm({...returnForm, return_type: v, from_hospital_id: '', from_warehouse_id: ''})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hospital_to_warehouse">{isRTL ? 'موقع توزيع → مستودع' : 'Location → Warehouse'}</SelectItem>
                  <SelectItem value="warehouse_to_warehouse">{isRTL ? 'مستودع → مستودع' : 'Warehouse → Warehouse'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {returnForm.return_type === 'hospital_to_warehouse' ? (
              <div className="space-y-2">
                <Label>{isRTL ? 'من موقع توزيع' : 'From Distribution Location'}</Label>
                <Select value={returnForm.from_hospital_id} onValueChange={(v) => setReturnForm({...returnForm, from_hospital_id: v})}>
                  <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر' : 'Select'} /></SelectTrigger>
                  <SelectContent>{hospitals.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{isRTL ? 'من مستودع' : 'From Warehouse'}</Label>
                <Select value={returnForm.from_warehouse_id} onValueChange={(v) => setReturnForm({...returnForm, from_warehouse_id: v})}>
                  <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر' : 'Select'} /></SelectTrigger>
                  <SelectContent>{activeWarehouses.filter(w => w.id !== returnForm.to_warehouse_id).map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.current_stock})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>{isRTL ? 'إلى مستودع' : 'To Warehouse'}</Label>
              <Select value={returnForm.to_warehouse_id} onValueChange={(v) => setReturnForm({...returnForm, to_warehouse_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر' : 'Select'} /></SelectTrigger>
                <SelectContent>{activeWarehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.current_stock})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الكمية' : 'Quantity'}</Label>
              <Input type="number" min="1" value={returnForm.quantity} onChange={(e) => setReturnForm({...returnForm, quantity: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Input value={returnForm.notes} onChange={(e) => setReturnForm({...returnForm, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialog(false)}>{t('cancel')}</Button>
            <Button onClick={handleReturn} className="bg-amber-500 hover:bg-amber-600 gap-2"><Undo2 className="h-4 w-4" />{isRTL ? 'إرجاع' : 'Return'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel Export */}
      <Dialog open={exportDialog} onOpenChange={setExportDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-500" />{isRTL ? 'تقرير المعاملات' : 'Transaction Report'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{isRTL ? 'من تاريخ' : 'From Date'}</Label>
                <Input type="date" value={exportFilters.start_date} onChange={(e) => setExportFilters({...exportFilters, start_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'إلى تاريخ' : 'To Date'}</Label>
                <Input type="date" value={exportFilters.end_date} onChange={(e) => setExportFilters({...exportFilters, end_date: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'نوع المعاملة' : 'Transaction Type'}</Label>
              <Select value={exportFilters.txn_type} onValueChange={(v) => setExportFilters({...exportFilters, txn_type: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'الكل' : 'All'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  <SelectItem value="TRANSFER">{isRTL ? 'نقل' : 'Transfer'}</SelectItem>
                  <SelectItem value="DISTRIBUTION">{isRTL ? 'توزيع' : 'Distribution'}</SelectItem>
                  <SelectItem value="RETURN">{isRTL ? 'إرجاع' : 'Return'}</SelectItem>
                  <SelectItem value="ADJUSTMENT">{isRTL ? 'تعديل' : 'Adjustment'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'المستودع المصدر' : 'Source Warehouse'}</Label>
              <Select value={exportFilters.from_warehouse_id} onValueChange={(v) => setExportFilters({...exportFilters, from_warehouse_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'الكل' : 'All'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialog(false)}>{t('cancel')}</Button>
            <Button onClick={handleExport} className="bg-emerald-500 hover:bg-emerald-600 gap-2"><Download className="h-4 w-4" />{isRTL ? 'تحميل' : 'Download'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Create Distribution Location */}
      <Dialog open={createLocDialog} onOpenChange={setCreateLocDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRTL ? 'إضافة موقع توزيع' : 'Add Distribution Location'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'اسم الموقع' : 'Location Name'}</Label>
              <Input value={newLoc.name} onChange={(e) => setNewLoc({...newLoc, name: e.target.value})} placeholder={isRTL ? 'مثال: مستشفى حمد' : 'e.g. Hamad Hospital'} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'النوع' : 'Type'}</Label>
              <Select value={newLoc.type} onValueChange={(v) => setNewLoc({...newLoc, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hospital">{isRTL ? 'مستشفى' : 'Hospital'}</SelectItem>
                  <SelectItem value="health_center">{isRTL ? 'مركز صحي' : 'Health Center'}</SelectItem>
                  <SelectItem value="temporary_site">{isRTL ? 'موقع مؤقت' : 'Temporary Site'}</SelectItem>
                  <SelectItem value="committee">{isRTL ? 'لجنة' : 'Committee'}</SelectItem>
                  <SelectItem value="pharmacy">{isRTL ? 'صيدلية' : 'Pharmacy'}</SelectItem>
                  <SelectItem value="other">{isRTL ? 'أخرى' : 'Other'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'العنوان (اختياري)' : 'Address (optional)'}</Label>
              <Input value={newLoc.address} onChange={(e) => setNewLoc({...newLoc, address: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateLocDialog(false)}>{t('cancel')}</Button>
            <Button onClick={handleCreateLocation} className="bg-sky-500 hover:bg-sky-600">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Distribution Location */}
      <Dialog open={editLocDialog} onOpenChange={setEditLocDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRTL ? 'تعديل موقع التوزيع' : 'Edit Distribution Location'}</DialogTitle></DialogHeader>
          {editingLoc && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'اسم الموقع' : 'Location Name'}</Label>
                <Input value={editingLoc.name} onChange={(e) => setEditingLoc({...editingLoc, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'النوع' : 'Type'}</Label>
                <Select value={editingLoc.type} onValueChange={(v) => setEditingLoc({...editingLoc, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hospital">{isRTL ? 'مستشفى' : 'Hospital'}</SelectItem>
                    <SelectItem value="health_center">{isRTL ? 'مركز صحي' : 'Health Center'}</SelectItem>
                    <SelectItem value="temporary_site">{isRTL ? 'موقع مؤقت' : 'Temporary Site'}</SelectItem>
                    <SelectItem value="committee">{isRTL ? 'لجنة' : 'Committee'}</SelectItem>
                    <SelectItem value="pharmacy">{isRTL ? 'صيدلية' : 'Pharmacy'}</SelectItem>
                    <SelectItem value="other">{isRTL ? 'أخرى' : 'Other'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'العنوان' : 'Address'}</Label>
                <Input value={editingLoc.address} onChange={(e) => setEditingLoc({...editingLoc, address: e.target.value})} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLocDialog(false)}>{t('cancel')}</Button>
            <Button onClick={handleEditLocation} className="bg-sky-500 hover:bg-sky-600">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
